/* eslint-disable max-len */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { FullPromotion } from '@1po/1po-bff-fe-spec/generated/backoffice/promotion/model/FullPromotion';
import { Promotion } from '@1po/1po-bff-fe-spec/generated/backoffice/promotion/model/Promotion';
import { ROUTER_BACKOFFICE_PROMOTION } from 'app/AppRouter';
import { RootState } from 'app/AppStore';
import { DataContainer, ErrorWithLabel } from 'components/DataContainer';
import Loader from 'components/Loader';
import { getCorrectPromotionView, toFullPromotionView } from 'domains/promotion/Promotion.mapper';
import { useFetchFullPromotion, useFetchPromotionDetails } from 'domains/promotion/Promotion.requests';
import { getCurrentPromotionDetails, getFullPromotion } from 'domains/promotion/Promotion.store';
import { PromotionAdditionalDiscountPreviewParam } from 'domains/promotion/Promotion.types';
import { Flex, Link, MarginBox, Text } from 'UI';
import { getData, hasData } from 'utils';
import { useResetScroll } from 'utils/hooks/useResetScroll';
import { PromotionPageView } from './PromotionPageView';

const PromotionDetailLoader = () => <Loader height={'50vh'} />;

const getDisplayPromotionPreview = (previewParam: string | null): boolean | undefined => {
  if (previewParam === null) {
    return undefined;
  }
  return previewParam === 'true';
};

const getAddToCartDisabled = (
  plannedPreview: boolean,
  displayPromotionPreview: boolean | undefined = undefined,
  adjustmentState: boolean | undefined = undefined,
): boolean => {
  if (plannedPreview) {
    return true;
  }
  if (displayPromotionPreview === undefined || adjustmentState === undefined) {
    return false;
  }
  return adjustmentState !== displayPromotionPreview;
};

const getPromotionView = (
  plannedPreview: boolean,
  promotion: Promotion | undefined,
  fullPromotion: FullPromotion | undefined,
  displayPromotionPreview: boolean | undefined = undefined,
): Promotion | undefined => {
  if (plannedPreview) {
    return toFullPromotionView(fullPromotion);
  }
  return getCorrectPromotionView(promotion, fullPromotion, displayPromotionPreview);
};

const isPlannedPreviewUnavailable = (plannedPreview: boolean, fullPromotion: FullPromotion | undefined): boolean => {
  if (!plannedPreview) {
    return false;
  }
  return fullPromotion?.status !== 'PLANNED';
};

const getPromotionData = <PlannedData, PublicData>(
  plannedPreview: boolean,
  plannedData: PlannedData,
  publicData: PublicData,
): PlannedData | PublicData => {
  if (plannedPreview) {
    return plannedData;
  }
  return publicData;
};

const PlannedPreviewUnavailable = () => {
  const { t } = useTranslation();

  return (
    <Flex direction={'column'} justify={'center'}>
      <MarginBox mt={90} />
      <ErrorWithLabel
        label={t(
          'backoffice.promotion.planned_preview.unavailable',
          'This promotion is no longer available for preview.',
        )}
      />
      <MarginBox mt={15} />
      <Flex justify={'center'}>
        <Link to={ROUTER_BACKOFFICE_PROMOTION}>
          <Text type={'text'} displayStyle={'link'} hoverUnderLine cursor={'pointer'}>
            {t('backoffice.promotion.planned_preview.back_to_dashboard', 'Back to promotions')}
          </Text>
        </Link>
      </Flex>
    </Flex>
  );
};

const PromotionDetailContent = ({
  unavailable,
  promotionView,
  addToCartDisabled,
  plannedPreview,
}: {
  unavailable: boolean;
  promotionView: Promotion | undefined;
  addToCartDisabled: boolean;
  plannedPreview: boolean;
}) => {
  if (unavailable) {
    return <PlannedPreviewUnavailable />;
  }
  return (
    <PromotionPageView
      promotion={promotionView}
      addToCartDisabled={addToCartDisabled}
      hideCartActions={plannedPreview}
    />
  );
};

const PromotionDetailPage = ({ plannedPreview = false }: { plannedPreview?: boolean }) => {
  const { promotionId } = useParams<{ promotionId: string }>();
  const promotionData = useSelector((state: RootState) => getCurrentPromotionDetails(state, promotionId));
  const promotion = getData(promotionData);
  const fullPromotionData = useSelector((state: RootState) => getFullPromotion(state, promotionId));
  const fullPromotion = getData(fullPromotionData);

  const params = new URLSearchParams(location.search);
  const previewParam = params.get(PromotionAdditionalDiscountPreviewParam);
  const displayPromotionPreview = getDisplayPromotionPreview(previewParam);
  const adjustmentState = promotion?.isAdjusted ?? fullPromotion?.adjustmentState?.applyDiscount;
  const addToCartDisabled = getAddToCartDisabled(plannedPreview, displayPromotionPreview, adjustmentState);
  const promotionView = getPromotionView(plannedPreview, promotion, fullPromotion, displayPromotionPreview);
  const unavailable = isPlannedPreviewUnavailable(plannedPreview, fullPromotion);
  const data = getPromotionData(plannedPreview, fullPromotionData, promotion);

  useFetchPromotionDetails(promotionId, hasData(promotion) || plannedPreview);
  useFetchFullPromotion(promotionId, { includePublicPromotion: false, skip: !plannedPreview });

  useResetScroll();

  return (
    <DataContainer data={data} Loading={PromotionDetailLoader}>
      <PromotionDetailContent
        unavailable={unavailable}
        promotionView={promotionView}
        addToCartDisabled={addToCartDisabled}
        plannedPreview={plannedPreview}
      />
    </DataContainer>
  );
};

export default PromotionDetailPage;
